import React, { useState } from 'react';
import { Alert, Box, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/en-gb';
import UpcomingEmployeeEvents from '../components/UpcomingEmployeeEvents';
import { contractActionFacts } from '../../../utils/contractActionFacts';
import { toPickerValue } from '../../../utils/dateOnly';

dayjs.extend(utc);

/**
 * ADR-136 phase 5 — "Cancel Contract" modal content. B1 pointed at "now": the
 * §5a batch with forceFinalization when the cancellation date is today/past
 * (the contract terminates immediately, episode closed AT the cancel date);
 * a future date reschedules the end and the scheduler enacts it.
 */
const EmployeeCancelContract = ({ employee, contract = null, episodeId = null, onValidChange }) => {
  const [formData, setFormData] = useState({
    cancelDate: dayjs().format('YYYY-MM-DD'),
    reason: ''
  });
  const [touched, setTouched] = useState({ cancelDate: false, reason: false });

  // ADR-187 — the acting contract's own facts (passed by ContractActionModals);
  // the person's flat fields are the PRIMARY's and are read only through the
  // resolver, when this is the primary or the payload has no contracts[].
  const facts = contract || contractActionFacts(employee, episodeId ? { id: episodeId } : null);
  const contractStartDate = facts.startDate;
  const currentEndDate = facts.endDate;
  const pendingRenewalOn = facts.pendingRenewalOn;

  const isOnOrAfterStart = (dateStr) => (
    !contractStartDate || !dateStr
      ? true
      : !dayjs.utc(dateStr).isBefore(dayjs.utc(contractStartDate), 'day')
  );

  const computeValidity = (data) => Boolean(
    data.cancelDate && data.reason && isOnOrAfterStart(data.cancelDate)
  );

  const handleDateChange = (newValue) => {
    const newDate = newValue && typeof newValue.isValid === 'function' && newValue.isValid()
      ? newValue.format('YYYY-MM-DD')
      : null;
    const newFormData = { ...formData, cancelDate: newDate };
    setFormData(newFormData);
    setTouched(prev => ({ ...prev, cancelDate: true }));
    onValidChange(computeValidity(newFormData), newFormData);
  };

  const handleInputChange = (field) => (event) => {
    const newFormData = { ...formData, [field]: event.target.value };
    setFormData(newFormData);
    setTouched(prev => ({ ...prev, [field]: true }));
    onValidChange(computeValidity(newFormData), newFormData);
  };

  React.useEffect(() => {
    // Initialize as invalid (reason still empty; the date defaults to today)
    onValidChange(false, formData);
  }, []);

  const isTodayOrPast = formData.cancelDate
    && formData.cancelDate <= dayjs().format('YYYY-MM-DD');
  const dateError = formData.cancelDate && !isOnOrAfterStart(formData.cancelDate);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
        <Alert severity={isTodayOrPast ? 'warning' : 'info'}>
          This <strong>ends the contract early</strong>.{' '}
          {isTodayOrPast ? (
            <>With today&apos;s (or a past) date, employment is terminated <strong>immediately</strong>:
            all project allocations, assignments, position/grade history and manager records are
            closed as of the cancellation date, and the person becomes resigned.</>
          ) : (
            <>With a future date, the contract end is rescheduled to that date and enacted by
            the scheduler when it arrives — nothing is terminated today.</>
          )}
          {' '}To only move the end date of a continuing contract, use <em>Change End Date</em>.
        </Alert>
        {/* ADR-136 §15 — fresh-fetched standing schedule. Scoped to the
            acting contract when known (ADR-113 §8). */}
        <UpcomingEmployeeEvents employeeId={employee?.id} episodeId={episodeId} />
        {/* ADR-136 phase 6 (§15 slice 3 refinement): cancelling the contract
            does not cancel a scheduled replacement — it will still rehire. */}
        {pendingRenewalOn && (
          <Alert severity="warning">
            A replacement is <strong>scheduled
            for {dayjs.utc(pendingRenewalOn).format('DD/MM/YYYY')}</strong> and
            cancelling this contract does not remove it: if employment ends before that date, the
            replacement still fires then and <strong>starts the new contract automatically</strong>.
            If the replacement is no longer wanted, use <em>Cancel Scheduled Renewal</em> first.
          </Alert>
        )}
        {currentEndDate && (
          <Typography variant="body2" color="text.secondary">
            Current end date: <strong>{dayjs.utc(currentEndDate).format('DD/MM/YYYY')}</strong>
            {contractStartDate && (
              <> — contract started {dayjs.utc(contractStartDate).format('DD/MM/YYYY')}</>
            )}
          </Typography>
        )}
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
          <DatePicker
            label="Cancellation Date"
            value={toPickerValue(formData.cancelDate)}
            onChange={handleDateChange}
            minDate={contractStartDate ? dayjs.utc(contractStartDate) : undefined}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: Boolean((touched.cancelDate && !formData.cancelDate) || dateError),
                helperText: dateError
                  ? 'The cancellation date cannot be before the contract start date'
                  : (isTodayOrPast
                    ? 'Today or past: the contract is terminated immediately, effective this date'
                    : 'Future date: the end is rescheduled and enacted by the scheduler'),
                inputProps: { placeholder: 'DD/MM/YYYY' }
              }
            }}
          />
        </LocalizationProvider>

        <TextField
          fullWidth
          label="Reason"
          value={formData.reason}
          onChange={handleInputChange('reason')}
          multiline
          rows={3}
          required
          error={touched.reason && !formData.reason}
          helperText={touched.reason && !formData.reason
            ? 'Reason is required'
            : 'Why the contract is being cancelled — becomes the termination reason'}
          placeholder="Enter reason for cancelling the contract..."
        />
      </Box>
    </Box>
  );
};

export default EmployeeCancelContract;
